import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import AvatarEditor from '../components/AvatarEditor';
import { Group, NumberField, Row, SectionTitle, Stepper } from '../components/SettingsRows';
import { useApp, useAppPath } from '../lib/AppContext';
import { ageOn, today } from '../lib/dates';
import DateField, { DOB_DEFAULT_MONTH } from '../components/DateField';

const FIELDS = ['name', 'weight_kg', 'height_cm', 'training_days_per_week', 'sex', 'date_of_birth'];

// "Hồ sơ", opened from "Sửa hồ sơ" in Settings: picture, name and body data. Weight, height and training
// days go to the AI for weekly menus and goal suggestions; sex and date of birth (the age is worked out from
// it, so nobody has to update it) only for goal suggestions.
export default function ProfilePage() {
  const { api, setProfile } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const path = useAppPath();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (data) => {
    const values = Object.fromEntries(FIELDS.map((key) => [key, data[key] ?? '']));
    setSaved(values);
    setForm(values);
    setProfile((p) => ({ ...p, name: data.name, avatar_url: data.avatar_url, needsName: data.needs_name }));
  };
  useEffect(() => { api.get('/profile').then(load); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      load(await api.patch('/profile', form));
      setStatus({ ok: true, text: 'Đã lưu' });
    } catch (e) {
      setStatus({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const back = () => (location.key !== 'default' ? navigate(-1) : navigate(path('/settings')));
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const dirty = form && FIELDS.some((key) => String(form[key]) !== String(saved[key]));
  const age = form?.date_of_birth ? ageOn(form.date_of_birth) : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="pt-safe flex items-center gap-1 pt-4">
        <button type="button" className="btn btn-circle btn-ghost -ml-2 size-11" aria-label="Quay lại" onClick={back}>
          <Icon name="chevronLeft" className="size-6" />
        </button>
        <h1 className="text-[22px] font-bold">Hồ sơ</h1>
      </div>
      {!form ? <div className="skeleton mt-3 h-96 rounded-[20px]" /> : (
        <form onSubmit={save}>
          <div className="pt-3"><AvatarEditor name={form.name} /></div>

          <SectionTitle>Thông tin</SectionTitle>
          <Group>
            <Row label="Tên" htmlFor="name">
              <input id="name" autoComplete="name" className="input h-11 w-40 rounded-[12px] border-line text-right text-base font-semibold"
                value={form.name} onChange={(e) => set('name')(e.target.value)} />
            </Row>
            <Row label="Giới tính">
              <div role="radiogroup" aria-label="Giới tính" className="grid grid-cols-2 gap-1 rounded-[12px] bg-base-200 p-1">
                {[['male', 'Nam'], ['female', 'Nữ']].map(([id, label]) => (
                  <button key={id} type="button" role="radio" aria-checked={form.sex === id} onClick={() => set('sex')(id)}
                    className={`h-9 rounded-[9px] px-4 text-sm font-semibold ${form.sex === id ? 'bg-neutral text-neutral-content' : ''}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Row>
            <Row label="Ngày sinh" hint={age != null ? `${age} tuổi` : null} htmlFor="dob">
              <DateField id="dob" title="Ngày sinh" placeholder="Chọn" min="1900-01-01" max={today()} value={form.date_of_birth}
                onChange={set('date_of_birth')} defaultMonth={DOB_DEFAULT_MONTH} startView="years"
                className="w-44 justify-end rounded-[12px] font-semibold" />
            </Row>
          </Group>

          <SectionTitle>Cơ thể và tập luyện</SectionTitle>
          <Group>
            <Row label="Cân nặng" htmlFor="weight">
              <NumberField id="weight" unit="kg" decimal value={form.weight_kg} onChange={set('weight_kg')} />
            </Row>
            <Row label="Chiều cao" htmlFor="height">
              <NumberField id="height" unit="cm" value={form.height_cm} onChange={set('height_cm')} />
            </Row>
            {/* Sent to the AI with every weekly menu, so the menu's note only needs this week's wishes. */}
            <Row label="Buổi tập mỗi tuần">
              <Stepper value={form.training_days_per_week} onChange={set('training_days_per_week')} />
            </Row>
          </Group>

          <div className="flex flex-col gap-2 pt-5">
            {status && <p role="status" className={`text-center text-sm font-medium ${status.ok ? 'text-brand-text' : 'text-error'}`}>{status.text}</p>}
            <button type="submit" className="btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy || !dirty}>
              {busy && <span className="loading loading-spinner loading-sm" />} Lưu thay đổi
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
